import React, { useState } from "react";
import { motion } from "framer-motion";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { FaEye } from "react-icons/fa6";
import DeleteIcon from "SVGS/DeleteIcon";
import SuperAccordion from "Components/SuperAccordion/SuperAccordion";
import ImageUploadBox from "Common/ImageUploadBox/ImageUploadBox";
import InsuranceCardPicPopup from "Popups/InsuranceCardPicPopup/InsuranceCardPicPopup";
import PropTypes from "prop-types";

const getCardUrl = (file) => {
  if (!file || file.type === "application/pdf") return null;
  if (!file._url) {
    file._url = URL.createObjectURL(file);
  }
  return file._url;
};

const formatFileSize = (bytes) => {
  if (!bytes) return "0 MB";
  const sizeInMB = (bytes / (1024 * 1024)).toFixed(2);
  return `${sizeInMB} MB`;
};

/**
 * Reusable insurance card upload section (front/back).
 * Pass planIndex when multiple plans can appear on the same page (e.g. Edit popup)
 * so file input IDs stay unique. Omit for single-plan flows (e.g. New Patient).
 */
const InsuranceCard = ({
  planIndex,
  insuranceSectionsOpen,
  onToggleInsuranceSection,
  insuranceCardFront,
  insuranceCardBack,
  handleCardUpload,
  handleCardRemove,
}) => {
  const [insCardPicPopup, setInsCardPicPopup] = useState({
    status: false,
    cardPic: "",
    picExt: "",
  });

  const frontInputId = planIndex != null ? `insurance-card-front-${planIndex}` : "insurance-card-front";
  const backInputId = planIndex != null ? `insurance-card-back-${planIndex}` : "insurance-card-back";

  const openInsCardPicPopup = (file) => {
    const previewUrl = URL.createObjectURL(file);
    const fileExt =
      file.type === "application/pdf"
        ? "pdf"
        : file.name.split(".").pop()?.toLowerCase() || "";
    setInsCardPicPopup({ status: true, cardPic: previewUrl, picExt: fileExt });
  };

  const closeInsCardPicPopup = () => {
    if (insCardPicPopup.cardPic) {
      URL.revokeObjectURL(insCardPicPopup.cardPic);
    }
    setInsCardPicPopup({ status: false, cardPic: "", picExt: "" });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="border rounded-lg p-4"
    >
      <div
        className="flex items-center justify-between cursor-pointer py-2"
        onClick={() => onToggleInsuranceSection("cards")}
      >
        <h3 className="text-lg font-medium text-gray-900">
          Insurance Card Images
        </h3>
        {insuranceSectionsOpen.cards ? (
          <FiChevronUp className="text-gray-500" />
        ) : (
          <FiChevronDown className="text-gray-500" />
        )}
      </div>
      <SuperAccordion show={insuranceSectionsOpen.cards}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 mt-4">
          <div>
            {insuranceCardFront ? (
              <div
                className="relative object-center w-full h-40 bg-center bg-no-repeat bg-cover rounded cursor-pointer shadow-1 border-2 border-gray-300"
                style={{
                  backgroundImage:
                    insuranceCardFront.type === "application/pdf"
                      ? "none"
                      : getCardUrl(insuranceCardFront)
                        ? `url("${getCardUrl(insuranceCardFront)}")`
                        : "none",
                }}
              >
                {insuranceCardFront.type === "application/pdf" ? (
                  <div className="w-full h-full flex items-center justify-center bg-primaryLightColor rounded">
                    <div className="flex w-full items-center px-4">
                      <div className="p-4 m-2 bg-white rounded-md text-primary flex items-center justify-center flex-shrink-0">
                        <img src="/img/file.svg" alt="file" />
                      </div>
                      <div className="flex flex-col justify-center flex-1 min-w-0">
                        <span className="text-sm font-semibold truncate pr-2">
                          {insuranceCardFront.name ||
                            "Insurance Card Front.pdf"}
                        </span>
                        <span className="text-sm text-secondaryLight">
                          {formatFileSize(insuranceCardFront.size)}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-between px-4 transition-all bg-transparent cursor-pointer group hover:bg-white/70">
                  <DeleteIcon
                    onClick={() => handleCardRemove("front")}
                    className="hidden cursor-pointer group-hover:inline text-secondaryLight hover:text-primary"
                    size={20}
                  />
                  <FaEye
                    onClick={() => openInsCardPicPopup(insuranceCardFront)}
                    className="hidden cursor-pointer group-hover:inline text-secondaryLight hover:text-primary"
                    size={20}
                  />
                </div>
              </div>
            ) : (
              <ImageUploadBox
                label="Front of Card"
                file={insuranceCardFront}
                fileInputId={frontInputId}
                onFileChange={(e) => handleCardUpload(e, "front")}
                onRemove={() => handleCardRemove("front")}
                accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
              />
            )}
          </div>
          <div>
            {insuranceCardBack ? (
              <div
                className="relative object-center w-full h-40 bg-center bg-no-repeat bg-cover rounded cursor-pointer shadow-1 border-2 border-gray-300"
                style={{
                  backgroundImage:
                    insuranceCardBack.type === "application/pdf"
                      ? "none"
                      : getCardUrl(insuranceCardBack)
                        ? `url("${getCardUrl(insuranceCardBack)}")`
                        : "none",
                }}
              >
                {insuranceCardBack.type === "application/pdf" ? (
                  <div className="w-full h-full flex items-center justify-center bg-primaryLightColor rounded">
                    <div className="flex w-full items-center px-4">
                      <div className="p-4 m-2 bg-white rounded-md text-primary flex items-center justify-center flex-shrink-0">
                        <img src="/img/file.svg" alt="file" />
                      </div>
                      <div className="flex flex-col justify-center flex-1 min-w-0">
                        <span className="text-sm font-semibold truncate pr-2">
                          {insuranceCardBack.name ||
                            "Insurance Card Back.pdf"}
                        </span>
                        <span className="text-sm text-secondaryLight">
                          {formatFileSize(insuranceCardBack.size)}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-between px-4 transition-all bg-transparent cursor-pointer group hover:bg-white/70">
                  <DeleteIcon
                    onClick={() => handleCardRemove("back")}
                    className="hidden cursor-pointer group-hover:inline text-secondaryLight hover:text-primary"
                    size={20}
                  />
                  <FaEye
                    onClick={() => openInsCardPicPopup(insuranceCardBack)}
                    className="hidden cursor-pointer group-hover:inline text-secondaryLight hover:text-primary"
                    size={20}
                  />
                </div>
              </div>
            ) : (
              <ImageUploadBox
                label="Back of Card"
                file={insuranceCardBack}
                fileInputId={backInputId}
                onFileChange={(e) => handleCardUpload(e, "back")}
                onRemove={() => handleCardRemove("back")}
                accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
              />
            )}
          </div>
        </div>
        {insCardPicPopup.status && (
          <InsuranceCardPicPopup
            onClose={closeInsCardPicPopup}
            cardPic={insCardPicPopup.cardPic}
            picExt={insCardPicPopup.picExt}
          />
        )}
      </SuperAccordion>
    </motion.div>
  );
};

InsuranceCard.propTypes = {
  planIndex: PropTypes.number,
  insuranceSectionsOpen: PropTypes.object.isRequired,
  onToggleInsuranceSection: PropTypes.func.isRequired,
  insuranceCardFront: PropTypes.object,
  insuranceCardBack: PropTypes.object,
  handleCardUpload: PropTypes.func.isRequired,
  handleCardRemove: PropTypes.func.isRequired,
};

export default InsuranceCard;
